import { t } from "@lingui/macro";
import {
  Calendar,
  CaretDown,
  Check,
  Envelope,
  Flag,
  IdentificationCard,
  LockSimple,
  MapPin,
  PencilSimple,
  User,
  UsersThree,
  Warning,
} from "@phosphor-icons/react";
import { useTouchOptimization } from "@stylingcv/hooks";
import { basicsSchema } from "@stylingcv/schema";
import { defaultResumeData } from "@stylingcv/schema";
import { Button, Input, Label, Tooltip, TooltipProvider } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useResendVerificationEmail } from "@/client/services/auth";
import { useAuthStore } from "@/client/stores/auth";
import { useResumeStore } from "@/client/stores/resume";

import { CustomFieldsSection } from "./custom/section";
import { SocialMediaButtons } from "./custom/social-buttons";
import { PictureSection } from "./picture/section";
import { URLInput } from "./shared/url-input";

export const BasicsSection = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const user = useAuthStore((state) => state.user);
  const setValue = useResumeStore((state) => state.setValue);

  const basics = useResumeStore((state) => {
    try {
      return state.resume.data.basics;
    } catch {
      return defaultResumeData.basics;
    }
  });

  const [showMore, setShowMore] = useState(
    !!(basics.dateOfBirth || basics.idNumber || basics.nationality || basics.maritalStatus),
  );

  const { toast } = useToast();
  const { resendVerificationEmail } = useResendVerificationEmail();

  // Touch optimization
  const { inputProps } = useTouchOptimization();

  const updateBasics = (path: string, value: string) => {
    try {
      setValue(`basics.${path}`, value);
    } catch {
      // Silent fail
    }
  };

  const onResendVerificationEmail = async () => {
    try {
      const data = await resendVerificationEmail();
      toast({
        variant: "default",
        title: t`Verification email sent`,
        description: data.message,
      });
    } catch {
      toast({
        variant: "error",
        title: t`Failed to send verification email`,
        description: t`Please try again later.`,
      });
    }
  };

  const filledOptionalCount = [
    basics.dateOfBirth,
    basics.idNumber,
    basics.nationality,
    basics.maritalStatus,
  ].filter(Boolean).length;

  const labelClass = "text-[13px] font-medium text-[var(--fg-2,#404040)]";
  const inputClass =
    "border-[var(--border,#e5e5e5)] bg-white text-[var(--fg-1,#171717)] placeholder:text-[var(--fg-4,#a3a3a3)] focus-visible:border-[var(--accent,#268599)] focus-visible:ring-1 focus-visible:ring-[var(--accent,#268599)] transition-all";

  return (
    <section id="basics" className="flex h-full flex-col overflow-y-auto">
      {/* Header — matches section-base */}
      <header className="flex items-center gap-3 border-b border-[var(--border,#e5e5e5)] px-6 py-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100">
          <User size={18} className="text-[var(--fg-2,#404040)]" />
        </div>
        <h2 className="flex-1 text-2xl font-bold leading-tight text-[var(--fg-1,#171717)]">
          {t`Personal`}
        </h2>
        <button className="shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:text-gray-600">
          <PencilSimple size={16} />
        </button>
      </header>

      {/* Subtitle */}
      <p className="px-6 py-3 text-sm text-[var(--fg-3,#737373)]">
        {t`How recruiters reach you. Verified contact info builds trust — these fields render at the top of every resume.`}
      </p>

      {/* Card wrapper */}
      <div className="mx-5 mb-5 rounded-xl border border-[var(--border,#e5e5e5)] bg-white">
        <main className="flex flex-col gap-5 p-5">
        <PictureSection />

        {/* Full name */}
        <div className="space-y-1.5">
          <Label htmlFor="basics.name" className={labelClass}>
            {t`Full name`}
          </Label>
          <Input
            {...inputProps}
            id="basics.name"
            value={basics.name || ""}
            autoCapitalize="words"
            autoCorrect="off"
            hasError={!basicsSchema.pick({ name: true }).safeParse({ name: basics.name }).success}
            className={cn(inputClass)}
            onChange={(e) => updateBasics("name", e.target.value)}
          />
        </div>

        {/* Headline with char counter */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="basics.headline" className={labelClass}>
              {t`Headline/Job Title`}
            </Label>
            <span className="text-xs text-[var(--fg-4,#a3a3a3)]">
              {(basics.headline || "").length}/60
            </span>
          </div>
          <Input
            {...inputProps}
            id="basics.headline"
            value={basics.headline || ""}
            autoCapitalize="words"
            maxLength={60}
            className={cn(inputClass)}
            onChange={(e) => updateBasics("headline", e.target.value)}
          />
        </div>

        {/* CONTACT group */}
        <div className="space-y-4 border-t border-[var(--border,#e5e5e5)] pt-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--fg-4,#a3a3a3)]">
              {t`Contact`}
            </p>
            <p className="mt-0.5 text-[12px] font-medium text-emerald-600">
              {t`Required`} · {t`shown on resume header`}
            </p>
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="basics.email" className={labelClass}>
                {t`Email`}
              </Label>
              {isLoggedIn && (
                <TooltipProvider>
                  <Tooltip
                    content={t`Email addresses cannot be modified after registration. Each account is strictly for personal use only. Creating resumes for others is not permitted.`}
                  >
                    <LockSimple size={13} className="cursor-help text-amber-500" />
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="relative">
              <Envelope
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4,#a3a3a3)]"
              />
              <Input
                {...inputProps}
                id="basics.email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="your@email.com"
                value={basics.email || ""}
                disabled={isLoggedIn}
                hasError={
                  !basicsSchema.pick({ email: true }).safeParse({ email: basics.email }).success
                }
                className={cn(inputClass, "pl-9", isLoggedIn && "cursor-not-allowed opacity-70")}
                onChange={(e) => updateBasics("email", e.target.value)}
              />
            </div>
            {/* Verification status */}
            {isLoggedIn && user && (
              <div
                className={cn(
                  "flex items-center gap-1.5 text-xs",
                  user.emailVerified ? "text-emerald-600" : "text-amber-600",
                )}
              >
                {user.emailVerified ? <Check size={12} /> : <Warning size={12} />}
                <span className="font-medium">
                  {user.emailVerified ? t`Email verified` : t`Email not verified`}
                </span>
                {!user.emailVerified && (
                  <>
                    <span className="text-gray-300">·</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs text-[var(--accent,#268599)]"
                      onClick={onResendVerificationEmail}
                    >
                      {t`Send verification`}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="basics.phone" className={labelClass}>
              {t`Phone`}
            </Label>
            <Input
              {...inputProps}
              id="basics.phone"
              type="tel"
              inputMode="tel"
              placeholder="+1 (123) 456 7890"
              value={basics.phone || ""}
              className={inputClass}
              style={{ direction: "ltr" }}
              onChange={(e) => updateBasics("phone", e.target.value)}
            />
          </div>

          {/* Website */}
          <div className="space-y-1.5">
            <Label htmlFor="basics.url" className={labelClass}>
              {t`Website`}
            </Label>
            <URLInput
              id="basics.url"
              value={basics.url}
              placeholder="https://example.com"
              onChange={(value) => {
                updateBasics("url", value as unknown as string);
              }}
            />
          </div>

          {/* Location */}
          <div className="space-y-1.5">
            <Label htmlFor="basics.location" className={labelClass}>
              {t`Location`}
            </Label>
            <div className="relative">
              <MapPin
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4,#a3a3a3)]"
              />
              <Input
                {...inputProps}
                id="basics.location"
                placeholder={t`City, Country`}
                value={basics.location || ""}
                autoCapitalize="words"
                className={cn(inputClass, "pl-9")}
                onChange={(e) => updateBasics("location", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* MORE PERSONAL DETAILS */}
        <div className="space-y-3">
          <button
            className="flex w-full items-start gap-3 rounded-xl border border-[var(--border,#e5e5e5)] bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50"
            onClick={() => setShowMore(!showMore)}
          >
            <CaretDown
              size={16}
              className={cn(
                "mt-0.5 shrink-0 text-[var(--fg-4,#a3a3a3)] transition-transform",
                showMore && "rotate-180",
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-[var(--fg-1,#171717)]">
                {t`More personal details`}
              </p>
              <p className="mt-0.5 text-[11px] text-[var(--fg-3,#737373)]">
                {t`Hidden from resume by default — add region-specific fields if your market expects them`}
              </p>
            </div>
            {filledOptionalCount > 0 && (
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[var(--accent,#268599)] text-[10px] font-bold text-white">
                {filledOptionalCount}
              </span>
            )}
          </button>

          {showMore && (
            <div className="space-y-4 rounded-xl border border-[var(--border,#e5e5e5)] bg-white p-4">
              {/* Date of birth */}
              <div className="space-y-1.5">
                <Label htmlFor="basics.dateOfBirth" className={labelClass}>
                  {t`Date of birth`}
                </Label>
                <div className="relative">
                  <Calendar
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4,#a3a3a3)]"
                  />
                  <Input
                    {...inputProps}
                    id="basics.dateOfBirth"
                    type="date"
                    value={basics.dateOfBirth || ""}
                    className={cn(inputClass, "pl-9 [color-scheme:light]")}
                    onChange={(e) => updateBasics("dateOfBirth", e.target.value)}
                  />
                </div>
              </div>

              {/* Nationality */}
              <div className="space-y-1.5">
                <Label htmlFor="basics.nationality" className={labelClass}>
                  {t`Nationality`}
                </Label>
                <div className="relative">
                  <Flag
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4,#a3a3a3)]"
                  />
                  <Input
                    {...inputProps}
                    id="basics.nationality"
                    value={basics.nationality || ""}
                    className={cn(inputClass, "pl-9")}
                    onChange={(e) => updateBasics("nationality", e.target.value)}
                  />
                </div>
              </div>

              {/* ID Number */}
              <div className="space-y-1.5">
                <Label htmlFor="basics.idNumber" className={labelClass}>
                  {t`ID Number`}
                </Label>
                <div className="relative">
                  <IdentificationCard
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4,#a3a3a3)]"
                  />
                  <Input
                    {...inputProps}
                    id="basics.idNumber"
                    value={basics.idNumber || ""}
                    className={cn(inputClass, "pl-9")}
                    onChange={(e) => updateBasics("idNumber", e.target.value)}
                  />
                </div>
              </div>

              {/* Marital status */}
              <div className="space-y-1.5">
                <Label htmlFor="basics.maritalStatus" className={labelClass}>
                  {t`Marital status`}
                </Label>
                <div className="relative">
                  <UsersThree
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4,#a3a3a3)]"
                  />
                  <Input
                    {...inputProps}
                    id="basics.maritalStatus"
                    value={basics.maritalStatus || ""}
                    className={cn(inputClass, "pl-9")}
                    onChange={(e) => updateBasics("maritalStatus", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SOCIAL PROFILES */}
        <div className="space-y-4 border-t border-[var(--border,#e5e5e5)] pt-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--fg-4,#a3a3a3)]">
            {t`Social Profiles`}
          </p>
          <CustomFieldsSection />
          <SocialMediaButtons />
        </div>
        </main>
      </div>
    </section>
  );
};
